import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Button, Tag } from "antd";
import { useGetIncomingPostbackLogQuery } from "api/TenantSlug/IncomingPostbackLog/hooks";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList";
import { QueryState } from "components/UI/QueryState/QueryState";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { ApiResponseBodyCell } from "components/UI/Table/UI/ApiResponseBodyCell/ApiResponseBodyCell";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { SearchFilterEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import IncomingPostbackLogData = App.Data.IncomingPostbackLogData;

interface IncomingPostbackLogDetailDrawerProps {
    logId: string;
}

const LOG_DETAIL_INCLUDES =
    "incomingPostback,conversionState,conversionState.owner,conversionState.advertiser,conversionState.offer";

export const IncomingPostbackLogDetailDrawer: FC<IncomingPostbackLogDetailDrawerProps> = ({ logId }) => {
    const { t } = usePaldockTranslation("drawers.incoming-postback-log-detail");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGA } = usePaldockTranslation("drawers.general-actions");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tEnum } = usePaldockTranslation("general");
    const drawerId = DrawerId.IncomingPostbackLogDetail;
    const INCOMING_EID_FILTER = `${TableId.IncomingPostbackStepLogTable}.eid.${SearchFilterEnum.Equal}`;

    const getIncomingPostbackLogQuery = useGetIncomingPostbackLogQuery(logId, {
        filter: {
            include: LOG_DETAIL_INCLUDES,
        },
    });
    const log = getIncomingPostbackLogQuery.data?.incomingPostbackLog;

    const logList = log
        ? PropertiesList.create<IncomingPostbackLogData>(log, [
              {
                  term: tG("date"),
                  description: ({ created_at }) => (created_at ? <DateTimeCell value={created_at} /> : <EmptyCell />),
              },
              {
                  term: tG("origin_id"),
                  description: ({ conversionState }) =>
                      conversionState?.origin_eid ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionState.origin_eid} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("event_id"),
                  description: ({ conversionState }) =>
                      conversionState?.eid ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionState.eid} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("external_id"),
                  description: ({ advertiser_custom_id }) =>
                      advertiser_custom_id ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={advertiser_custom_id} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("action"),
                  description: ({ action }) => (action ? action : <EmptyCell />),
              },
              {
                  term: tG("source"),
                  description: ({ source }) => (source ? source : <EmptyCell />),
              },
              {
                  term: t("fields.transaction"),
                  description: ({ conversionState }) => conversionState?.type ?? <EmptyCell />,
              },
              {
                  term: tG("affiliate"),
                  description: ({ conversionState }) => <EntityLinkCell.Owner entity={conversionState?.owner} />,
              },
              {
                  term: tG("advertiser"),
                  description: ({ conversionState }) => (
                      <EntityLinkCell.Advertiser entity={conversionState?.advertiser} />
                  ),
              },
              {
                  term: tG("offer"),
                  description: ({ conversionState }) => <EntityLinkCell.Offer entity={conversionState?.offer} />,
              },
              {
                  term: t("fields.name"),
                  description: ({ incomingPostback }) => incomingPostback?.name ?? <EmptyCell />,
              },
              {
                  term: t("fields.state"),
                  description: ({ state }) => (
                      <Tag
                          icon={state ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          color={state ? "green" : "red"}
                      >
                          {state ? tGL("ok") : tGL("fail")}
                      </Tag>
                  ),
              },
              {
                  term: t("fields.output"),
                  description: ({ output }) => (output ? tEnum(`postback_log_output.${output}`) : <EmptyCell />),
              },
              {
                  term: t("fields.message"),
                  description: ({ message, id }) => (
                      <StepLogMessageCell message={message} id={`message-${id}`} lines={30} />
                  ),
              },
          ])
        : null;

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={[`${drawerId}-id`]}
            extraButtons={
                <>
                    <Button size="large" disabled>
                        {tGA("view_scenario_run")}
                    </Button>
                    <TenantLink
                        to="/log/tracking/s2s-postback-steps"
                        search={{ [INCOMING_EID_FILTER]: log?.conversionState?.eid ?? "" }}
                    >
                        <Button size="large">{tGA("view_step_logs")}</Button>
                    </TenantLink>
                </>
            }
        >
            {!log ? (
                <QueryState
                    isLoading={getIncomingPostbackLogQuery.isLoading}
                    isError={getIncomingPostbackLogQuery.isError || (!getIncomingPostbackLogQuery.isLoading && !log)}
                    errorKey="errors.something_went_wrong"
                />
            ) : (
                <DrawerContent className="flex flex-col gap-4 p-4">
                    <DrawerCollapsible title={t("sections.log")}>
                        <PropertiesList list={logList ?? []} />
                    </DrawerCollapsible>
                    <DrawerCollapsible title={t("sections.request_data")}>
                        <ApiResponseBodyCell response={{ body: log.data }} id={`request-data-${log.id}`} lines={30} />
                    </DrawerCollapsible>
                </DrawerContent>
            )}
        </Drawer>
    );
};
